import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { FacilityResponse, Schema } from "@bw/shared/types/generated";

import type {
  FacilityActionError,
  RunFacilityAction,
} from "./FacilityReviewDialogs";

export function FacilityAssessmentCenterPanel({
  error,
  facility,
  facilityAuthoritative,
  facilityRetrying,
  governancePending,
  onAction,
  onRetryFacility,
}: {
  error: FacilityActionError | null;
  facility: FacilityResponse;
  facilityAuthoritative: boolean;
  facilityRetrying: boolean;
  governancePending: boolean;
  onAction: RunFacilityAction;
  onRetryFacility: () => void;
}) {
  const visibleError =
    error?.facilityId === facility.facility_id && error.kind === "assessment"
      ? error
      : null;

  function update(enabled: boolean) {
    if (!facilityAuthoritative || governancePending) return;
    const payload: Schema["AssessmentCenterStatusUpdate"] = { enabled };
    onAction({
      kind: "assessment",
      facilityId: facility.facility_id,
      payload,
    });
  }

  return (
    <Paper
      component="section"
      aria-labelledby="assessment-center-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}
    >
      <Stack spacing={1.5}>
        <Typography
          id="assessment-center-heading"
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700 }}
        >
          Assessment center
        </Typography>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={facility.is_assessment_center}
                disabled={governancePending || !facilityAuthoritative}
                onChange={(_event, checked) => update(checked)}
              />
            }
            label="Assessment center"
          />
        </Box>
        {!facilityAuthoritative ? (
          <Alert
            severity="warning"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={facilityRetrying || governancePending}
                onClick={onRetryFacility}
              >
                Retry
              </Button>
            }
          >
            Facility data is unavailable or needs to be refreshed. Retry before
            changing this setting.
          </Alert>
        ) : visibleError ? (
          <Alert severity="error" role="alert">
            {visibleError.message}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
